import {Box, Button, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import TextFieldComponent from '@components/inputs/textField/TextField.component';

import {Sizes} from '@constants/sizes.constants';
import {Close} from '@icons/index';
import {ClientInvoiceAddress} from '@store/clients/details';
import MultiTextFieldComponent from '@components/inputs/multiTextField/MultiTextField.component';

interface ManageAddressDrawerProps {
  address?: ClientInvoiceAddress;
  handleAdd: (contact: ClientInvoiceAddress) => void;
  handleClose: () => void;
  readOnly?: boolean;
}

const ManageAddressDrawer = (props: ManageAddressDrawerProps) => {
  const {address: addressProps, handleAdd, handleClose, readOnly} = props;

  const {t} = useTranslation();

  const [address, setAddress] = useState<ClientInvoiceAddress>(addressProps || {});

  const updateAddress = (name: string) => (value: any) => setAddress((state) => ({...state, [name]: value}));

  const errors = !readOnly ? {
    name: getFieldError(address.name, FieldValidationType.REQUIRED_STRING),
    street: getFieldError(address.street, FieldValidationType.REQUIRED_STRING),
    city: getFieldError(address.city, FieldValidationType.REQUIRED_STRING),
    zip: getFieldError(address.zip, FieldValidationType.REQUIRED_STRING),
    country: getFieldError(address.country, FieldValidationType.REQUIRED_STRING),
    emails: address.emails?.some((e) => getFieldError(e, FieldValidationType.EMAIL)) ? getFieldError('', FieldValidationType.EMAIL) : undefined,
    ccEmails: address.ccEmails?.some((e) => getFieldError(e, FieldValidationType.EMAIL)) ? getFieldError('', FieldValidationType.EMAIL) : undefined,
  } : {};

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack flex={1} overflow="hidden" pr="10px">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb="12px" mr="-8px">
          <Typography fontSize={18} fontWeight="700">
            {t('unitDetails.addresses.drawerTitle')}
          </Typography>
          <IconButton onClick={handleClose}>
            <Close/>
          </IconButton>
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal"/>
        <Stack py="20px" spacing={2}>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.addresses.name')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('name')}
            value={address.name || ''}
            error={errors.name}
            readOnly
            disabled
            required/>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.addresses.lastname')}
            placeholder={t('global.writeSomething')}
            value={address.addressCode || ''}
            readOnly
            disabled
            required/>
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal"/>
        <Stack py="20px" spacing={2}>
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street1')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('street')}
            value={address?.street || ''}
            error={errors.street}
            readOnly
            disabled
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street2')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('street2')}
            value={address?.street2 || ''}
            readOnly
            disabled
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.street3')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('street3')}
            value={address?.street3 || ''}
            readOnly
            disabled
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.city')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('city')}
            value={address?.city || ''}
            error={errors.city}
            readOnly
            disabled
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.zip')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('zip')}
            value={address?.zip}
            error={errors.zip}
            readOnly
            disabled
          />
          <TextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.country')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('country')}
            value={address?.country || ''}
            error={errors.country}
            readOnly
            disabled
          />
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal"/>
        <Stack py="20px" spacing={2}>
          <MultiTextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.email')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('emails')}
            values={address?.emails || []}
            error={errors.emails}

          />
          <MultiTextFieldComponent
            labelWidth={Sizes.drawerInputLabelWidth}
            label={t('unitDetails.contacts.address.ccEmails')}
            placeholder={t('global.writeSomething')}
            handleChange={updateAddress('ccEmails')}
            values={address?.ccEmails || []}
            error={errors.ccEmails}

          />
        </Stack>
        <Divider variant="fullWidth" orientation="horizontal"/>
        {!readOnly && (
          <Box alignSelf="flex-end" justifySelf="flex-end" mt="40px" mb="20px">
            <Button
              variant="contained"
              color="primary"
              disabled={checkIfErrors(errors)}
              onClick={() => handleAdd(address)}>
              {t(`global.${addressProps ? 'modify' : 'add'}`)}
            </Button>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
};

export default ManageAddressDrawer;
